import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import type { BreadcrumbItem } from "@/components/Breadcrumbs";

type BreadcrumbContextValue = {
  customItems: BreadcrumbItem[];
  setCustomBreadcrumbs: (items: BreadcrumbItem[]) => void;
};

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [customItems, setCustomItems] = useState<BreadcrumbItem[]>([]);
  const setCustomBreadcrumbs = useCallback((items: BreadcrumbItem[]) => {
    setCustomItems(items);
  }, []);
  const value = useMemo(
    () => ({
      customItems,
      setCustomBreadcrumbs,
    }),
    [customItems, setCustomBreadcrumbs],
  );
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumbContext() {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error("useBreadcrumbContext must be used within a BreadcrumbProvider");
  }
  return context;
}

export function usePageBreadcrumbs(items?: BreadcrumbItem[] | null) {
  const { setCustomBreadcrumbs } = useBreadcrumbContext();
  useEffect(() => {
    if (items && items.length) {
      setCustomBreadcrumbs(items);
      return () => setCustomBreadcrumbs([]);
    }
    setCustomBreadcrumbs([]);
    return () => setCustomBreadcrumbs([]);
  }, [items, setCustomBreadcrumbs]);
}
